<template>
  <div class="page-wrap">

    <!-- Page header -->
    <div class="page-header">
      <div class="page-header__left">
        <h1 class="page-title">Booked List</h1>
        <span class="page-subtitle">Manage and track all bookings</span>
      </div>
      <div class="page-header__right">
        <button
            class="btn-export"
            @click="exportToCSV"
            v-if="orders && orders.length"
            title="Export to CSV"
        >
          <i class="fa fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-panel">
      <div class="filter-panel__row">
        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search by customer, order ID..."
                v-model="inputFilters.search"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Purchase Date</label>
          <date-picker
              v-model="inputFilters.book_date"
              locale="en"
              format="YYYY-MM-DD"
              range
              clearable
              color="#f42f4b"
              class="dd-datepicker"
          />
        </div>

        <div class="filter-field">
          <label class="filter-label">Service Date</label>
          <date-picker
              v-model="inputFilters.activity_date"
              locale="en"
              format="YYYY-MM-DD"
              range
              clearable
              color="#f42f4b"
              class="dd-datepicker"
          />
        </div>

        <div class="filter-field filter-field--sm">
          <label class="filter-label">Status</label>
          <select v-model="inputFilters.status" class="dd-input">
            <option value="">All</option>
            <option value="Booked">Booked</option>
            <option value="Canceled">Canceled</option>
          </select>
        </div>

        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>Cart ID</th>
            <th>Gift ID</th>
            <th>Order ID</th>
            <th>Customer</th>
            <th>Experience / City</th>
            <th>Supplier</th>
            <th>Purchase Date</th>
            <th>Service Date</th>
            <th>Status</th>
            <th>Tags</th>
            <th>Action</th>
          </tr>
          </thead>
          <tbody>
          <router-link
              tag="tr"
              :to="{ name: 'order.show', params: { id: order.id } }"
              v-for="order in orders"
              :key="order.id"
              class="dd-table__row"
          >
            <td>
                <span
                    class="link-cell"
                    @click.prevent="gotoCardPage(getCardId(order.tags))"
                >{{ getCardId(order.tags) }}</span>
            </td>
            <td>
                <span
                    class="link-cell"
                    @click.prevent="gotoGiftPage(getGiftId(order.tags))"
                >{{ getGiftId(order.tags) }}</span>
            </td>
            <td>
              <span class="order-id">#{{ order.id }}</span>
            </td>
            <td>{{ getOrderLeaderName(order.passengers) }}</td>
            <td>
              <div class="exp-name">
                {{ order.booked_details.product.title }}
                <span class="exp-level">({{ order.booked_details.service_level.title }})</span>
              </div>
              <div class="exp-city">
                <i class="fa fa-map-marker"></i>
                {{ order.booked_details.starting_point.city.name }}
              </div>
            </td>
            <td>{{ order.supplier.name }}</td>
            <td class="date-cell">{{ order.booked_at ? formatDate(order.booked_at) : '—' }}</td>
            <td class="date-cell">{{ formatDate(order.starts_at) }}</td>
            <td v-html="getOrderStatus(order.status)"></td>
            <td v-html="getOrderTagLabels(order.tags)"></td>
            <td>
              <button
                  class="btn-action"
                  @click.prevent="showSendTicketModal(order.id)"
              >
                <i class="fa fa-ticket"></i> Send Ticket
              </button>
            </td>
          </router-link>

          <!-- Empty state -->
          <tr v-if="orders && orders.length === 0">
            <td colspan="11" class="empty-state">
              <i class="fa fa-inbox"></i>
              <div>No orders found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">
          Showing {{ orders ? orders.length : 0 }} of {{ totalItems }} orders
        </div>
        <b-pagination
            v-model="page"
            :total-rows="totalItems"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

    <!-- Send Ticket Modal -->
    <modal name="send-ticket-modal" height="auto" :adaptive="true">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Send Ticket</span>
          <button class="dd-modal__close" @click="$modal.hide('send-ticket-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <p class="dd-modal__desc">
            Send ticket to the leader of order
            <span class="dd-modal__order-id">#{{ ticket.orderId }}</span>
          </p>
          <label class="filter-label">Ticket URL</label>
          <input
              v-model="ticket.ticketUrl"
              type="text"
              class="dd-input"
              placeholder="https://..."
          />
          <ul class="error-list" v-if="ticket.errors.length">
            <li v-for="error in ticket.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('send-ticket-modal')">Cancel</button>
          <button class="btn-submit" @click="sendTicket">
            <i class="fa fa-paper-plane"></i> Send
          </button>
        </div>
      </div>
    </modal>

  </div>
</template>

<script>
import { config } from "../helpers";

export default {
  name: "Orders",
  data() {
    return {
      url: "",
      orders: [],
      inputFilters: {
        search: null,
        status: "",
        activity_date: [],
        book_date: [],
      },
      isFiltered: false,
      filters: {},
      totalItems: 0,
      page: 1,
      lastPage: 1,
      perPage: 1,
      ticket: {
        orderId: null,
        ticketUrl: null,
        errors: [],
      },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/orders";
    this.loadOrders();
  },

  methods: {
    loadOrders() {
      let myData = { page: this.page };
      if (this.isFiltered) {
        myData = { ...myData, filters: this.filters };
      }
      axios.get(this.url, { params: myData }).then((response) => {
        this.orders = response.data.data;
        this.page = response.data.meta.current_page;
        this.lastPage = response.data.meta.last_page;
        this.totalItems = response.data.meta.total;
        this.perPage = response.data.meta.per_page;
      });
    },

    filter() {
      this.page = 1;
      this.isFiltered = true;
      let filters = {};
      if (this.inputFilters.search)   filters.search = this.inputFilters.search;
      if (this.inputFilters.status)   filters.status = this.inputFilters.status;
      if (this.inputFilters.activity_date[0]) {
        filters.activity_date = {
          start: this.inputFilters.activity_date[0],
          end:   this.inputFilters.activity_date[1],
        };
      }
      if (this.inputFilters.book_date[0]) {
        filters.book_date = {
          start: this.inputFilters.book_date[0],
          end:   this.inputFilters.book_date[1],
        };
      }
      this.filters = filters;
      this.loadOrders();
    },

    exportToCSV() {
      let myData = { page: this.page };
      if (this.isFiltered) myData = { ...myData, filters: this.filters };
      axios.get(this.url + "/export", { params: myData }).then((response) => {
        const url  = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "orders.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    },

    getCardId(tags) {
      return tags.find((t) => t.key === "dd_cart_id")?.value ?? "?";
    },
    getGiftId(tags) {
      return tags.find((t) => t.key === "dd_gift_id")?.value ?? "?";
    },
    gotoCardPage(id) { this.$router.push(`/carts/${id}`); },
    gotoGiftPage(id) { this.$router.push(`/gifts/${id}`); },

    getOrderLeaderName(passengers) {
      const leader = passengers.find((p) => p.is_leader);
      return leader ? `${leader.first_name} ${leader.last_name}` : "—";
    },

    formatDate(date) {
      if (!date) return "—";
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
    },

    getOrderStatus(status) {
      const map = {
        Booked:    ['status-badge status-badge--green',  'Booked'],
        InCart:    ['status-badge status-badge--gray',   'In Cart'],
        Pending:   ['status-badge status-badge--blue',   'Pending'],
        Canceled:  ['status-badge status-badge--red',    'Canceled'],
        Cancelled: ['status-badge status-badge--red',    'Canceled'],
      };
      const [cls, label] = map[status] ?? ['status-badge status-badge--gray', status];
      return `<span class="${cls}">${label}</span>`;
    },

    getOrderTagLabels(tags) {
      if (tags.some((t) => t.key.startsWith("dd_gift_"))) {
        return '<span class="tag-badge">Gift</span>';
      }
      return '<span class="no-tag">—</span>';
    },

    showSendTicketModal(orderId) {
      this.ticket = { orderId, ticketUrl: "", errors: [] };
      this.$modal.show("send-ticket-modal");
    },

    sendTicket(event) {
      event.preventDefault();
      this.ticket.errors = [];
      axios
          .post(`${config("BACKEND_ROOT")}/admin/orders/${this.ticket.orderId}/send-ticket`, {
            ticket_url: this.ticket.ticketUrl,
          })
          .then(() => {
            alert("Ticket has been sent!");
            this.$modal.hide("send-ticket-modal");
          })
          .catch((error) => {
            this.ticket.errors =
                error.response.status === 404
                    ? [error.response.data.meta.message]
                    : error.response.data.errors.ticket_url;
          });
    },
  },

  watch: {
    page() { this.loadOrders(); },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page layout
══════════════════════════════════════ */
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.page-title {
  font-size: 20px;
  font-weight: 500;
  color: var(--text-1);
  margin: 0 0 2px;
}
.page-subtitle {
  font-size: 12px;
  color: var(--text-3);
}

/* ══════════════════════════════════════
   Filter panel
══════════════════════════════════════ */
.filter-panel {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
}
.filter-panel__row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}
.filter-field       { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow { flex: 1; }
.filter-field--sm   { min-width: 130px; max-width: 160px; }
.filter-field--action { min-width: auto; }

.filter-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: .4px;
  margin-bottom: 5px;
}

/* ── Inputs ── */
.dd-input {
  height: 36px;
  background: var(--bg-base);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-1);
  font-size: 13px;
  padding: 0 10px;
  outline: none;
  width: 100%;
  transition: border-color .15s;
}
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }

.input-icon-wrap { position: relative; }
.input-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-3);
  font-size: 13px;
}
.dd-input--icon { padding-left: 32px; }

/* ── Buttons ── */
.btn-filter {
  height: 36px;
  padding: 0 16px;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity .15s;
}
.btn-filter:hover { opacity: .88; }

.btn-export {
  height: 36px;
  padding: 0 14px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-2);
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: border-color .15s, color .15s;
}
.btn-export:hover { border-color: var(--accent); color: var(--accent); }

/* ══════════════════════════════════════
   Table
══════════════════════════════════════ */
.table-panel {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
}

.table-responsive-wrap { overflow-x: auto; }

.dd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.dd-table thead tr {
  border-bottom: 1px solid var(--border);
}
.dd-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: var(--text-3);
  white-space: nowrap;
  text-align: left;
}
.dd-table td {
  padding: 12px 14px;
  color: var(--text-2);
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}
.dd-table__row { cursor: pointer; transition: background .12s; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table__row:last-child td { border-bottom: none; }

.link-cell {
  color: var(--accent);
  cursor: pointer;
  font-weight: 500;
}
.link-cell:hover { text-decoration: underline; }

.order-id { font-weight: 500; color: var(--text-1); }

.exp-name  { color: var(--text-1); font-weight: 500; margin-bottom: 2px; }
.exp-level { color: var(--text-3); font-weight: 400; font-size: 12px; }
.exp-city  { font-size: 11px; color: var(--text-3); }
.exp-city i { margin-right: 3px; }

.date-cell { font-size: 12px; color: var(--text-3); white-space: nowrap; }

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.status-badge--green { background: rgba(34,197,94,.12);  color: #22c55e; }
.status-badge--gray  { background: rgba(156,163,175,.12); color: #9ca3af; }
.status-badge--blue  { background: rgba(99,102,241,.12);  color: #818cf8; }
.status-badge--red   { background: rgba(244,47,75,.12);   color: #f42f4b; }

.tag-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(129,140,248,.12);
  color: #818cf8;
}
.no-tag { color: var(--text-3); }

/* Action button */
.btn-action {
  height: 30px;
  padding: 0 12px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-2);
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color .15s, color .15s;
}
.btn-action:hover { border-color: var(--accent); color: var(--accent); }

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem 1rem !important;
  color: var(--text-3);
}
.empty-state i {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

/* ══════════════════════════════════════
   Pagination
══════════════════════════════════════ */
.pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .75rem 1.25rem;
  border-top: 1px solid var(--border);
  flex-wrap: wrap;
  gap: 8px;
}
.pagination-info { font-size: 12px; color: var(--text-3); }

/* Bootstrap-Vue pagination override */
.dd-pagination >>> .page-link {
  background: var(--bg-hover);
  border-color: var(--border);
  color: var(--text-2);
  font-size: 13px;
}
.dd-pagination >>> .page-item.active .page-link {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}
.dd-pagination >>> .page-link:hover {
  background: var(--bg-hover);
  border-color: var(--accent);
  color: var(--accent);
}

/* ══════════════════════════════════════
   Modal
══════════════════════════════════════ */
.dd-modal {
  background: var(--bg-panel);
  border-radius: 12px;
  overflow: hidden;
}
.dd-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border);
}
.dd-modal__title { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close {
  width: 28px; height: 28px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--text-2);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: border-color .15s;
}
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body { padding: 1.25rem; }
.dd-modal__desc { font-size: 13px; color: var(--text-2); margin-bottom: 1rem; }
.dd-modal__order-id { color: var(--accent); font-weight: 500; }
.dd-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: .75rem 1.25rem;
  border-top: 1px solid var(--border);
}

.btn-cancel {
  height: 36px; padding: 0 16px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-2);
  font-size: 13px;
  cursor: pointer;
  transition: border-color .15s;
}
.btn-cancel:hover { border-color: var(--border-2); }

.btn-submit {
  height: 36px; padding: 0 16px;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  transition: opacity .15s;
}
.btn-submit:hover { opacity: .88; }

.error-list {
  margin-top: 10px;
  padding-left: 16px;
  color: var(--accent);
  font-size: 13px;
}

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field,
  .filter-field--sm,
  .filter-field--grow { width: 100%; max-width: 100%; }
}
</style>
